import dayjs from "dayjs";
import Highcharts from "highcharts";
import ReactDOMServer from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";

import type ColorStack from "app/components/colorsStack/ColorStack";

import { DEFAULT_UN_CHECK_COLOR, MAX_SELECTION, OTHER_COLOR, getTotalKeywordsName } from "../../consts";
import { formatVisits, formatVisitsWithThreshold } from "../helpers";

import VisitsOvertimeChartTooltip from "./components/VisitsOvertimeChartTooltip/VisitsOvertimeChartTooltip";
import type { ChartSeries, LegendItem, ProcessedData } from "./types";

interface CreateLegendItemOptions {
    name: string;
    visible: boolean;
    color?: string;
    visits: number;
    isTotal?: boolean;
    useThreshold?: boolean;
}
export const createLegendItem = ({
    name,
    visible,
    color,
    visits,
    isTotal = false,
    useThreshold = true,
}: CreateLegendItemOptions): LegendItem => {
    const finalColor = isTotal ? OTHER_COLOR : color || DEFAULT_UN_CHECK_COLOR;

    return {
        name,
        visible,
        assignedColor: finalColor,
        color: visible ? finalColor : DEFAULT_UN_CHECK_COLOR,
        data: useThreshold ? formatVisitsWithThreshold(visits) : formatVisits(visits),
        ...(isTotal && { isTotal }),
    };
};

export const createTopAndOtherLegendItems = (
    sources: {
        name: string;
        visits: number;
    }[],
    colorStack: React.MutableRefObject<ColorStack>,
    createLegendItemFn: typeof createLegendItem,
) => {
    const sourceMap = Object.fromEntries(sources.map((s) => [s.name, s]));
    const lobSources = sources.filter((s) => s.name !== getTotalKeywordsName());

    const top = lobSources
        .sort((a, b) => b.visits - a.visits)
        .slice(0, MAX_SELECTION)
        .map((s) => s.name);

    const other = lobSources
        .sort((a, b) => b.visits - a.visits)
        .filter((s) => !top.includes(s.name))
        .map((s) => s.name);

    const createLegendItems = (names: string[], visible: boolean) =>
        names.map((name) =>
            createLegendItemFn({
                name,
                visible,
                color: colorStack.current.acquire(),
                visits: sourceMap[name].visits,
            }),
        );

    return {
        items: [...createLegendItems(top, true), ...createLegendItems(other, false)],
        top,
        other,
        sourceMap,
    };
};

export const getChartConfig = () => ({
    legend: { enabled: false },
    xAxis: {
        type: "datetime",
        dateTimeLabelFormats: { month: "%b %y", day: "%b %e", year: "%Y" },
        labels: {
            style: {
                textTransform: "capitalize" as const,
                fontSize: "11px",
                color: colorsPalettes.carbon[300],
                fontFamily: "Roboto",
            },
            formatter() {
                return Highcharts.dateFormat("%b %y", this.value);
            },
        },
        crosshair: { width: 1 },
        lineColor: colorsPalettes.carbon[100],
        tickColor: colorsPalettes.carbon[100],
        gridLineWidth: 0,
    },
    yAxis: {
        tickAmount: 8,
        labels: {
            style: {
                fontSize: "11px",
                fontFamily: "Roboto",
                color: colorsPalettes.carbon[400],
            },
            formatter() {
                return formatVisits(this.value);
            },
        },
        gridLineWidth: 0.5,
    },
    tooltip: {
        shared: true,
        useHTML: true,
        outside: true,
        formatter() {
            const { points } = this;
            return ReactDOMServer.renderToString(<VisitsOvertimeChartTooltip record={{ points }} />);
        },
    },
    plotOptions: {
        series: {
            marker: {
                radius: 4.5,
                states: { hover: { enabled: true, lineWidth: 1, lineColor: colorsPalettes.carbon[0], radius: 6 } },
                enabled: false,
                symbol: "circle",
            },
            turboThreshold: 5000,
            fillOpacity: 1,
            animation: false,
        },
        area: {
            stacking: "normal",
            lineWidth: 0,
            marker: {
                enabled: false,
                states: {
                    hover: {
                        enabled: true,
                    },
                },
            },
        },
        line: {
            lineWidth: 2,
            connectNulls: false,
            marker: { enabled: true },
        },
    },
    chart: { height: 280, backgroundColor: "transparent", style: { fontFamily: "Roboto" } },
});

const buildChartSeries = (
    breakdown: Record<string, Record<string, { Visits: number }>> | null,
    siteVisits: Record<string, number>,
    legendItems: LegendItem[],
): ChartSeries[] => {
    if (!breakdown) {
        return [];
    }

    const dates = Object.keys(breakdown).sort();
    const timestamps = dates.map((d) => dayjs.utc(d).valueOf());
    const getDate = (ts: number) => dayjs.utc(ts).format("YYYY-MM-DD");

    return legendItems.map((item) => {
        const data = timestamps.map((ts) => {
            const date = getDate(ts);
            let y: number | null = null;

            if (item.isTotal) {
                const dateBreakdown = breakdown[date];
                if (dateBreakdown) {
                    y = Object.values(dateBreakdown).reduce((sum, siteData) => {
                        return sum + (siteData.Visits || 0);
                    }, 0);
                }
            } else {
                y = breakdown[date]?.[item.name]?.Visits ?? null;
            }

            return { x: ts, y };
        });

        return {
            name: item.name,
            color: item.assignedColor,
            visible: item.visible,
            data,
            isTotal: item.isTotal,
            type: item.isTotal ? "line" : "area",
        };
    });
};

export const createChartData = (processedData: ProcessedData | null, legendItems: LegendItem[]): ChartSeries[] => {
    if (!processedData) {
        return [];
    }
    return buildChartSeries(processedData.breakdown, processedData.siteVisits, legendItems);
};
